import supabase from '../supabase/supabase';
import { todayLocal } from '../lib/utils';

const TABLES = {
  TENANTS: 'tenants_1771793184',
  PROFILES: 'profiles_1771793184',
  MANUFACTURERS: 'manufacturers_1771793184',
  IMPORTERS: 'importers_1771793184',
  TYPES: 'firearm_types_1771793184',
  CALIBERS: 'calibers_1771793184',
  MODELS: 'firearm_models_1771793184',
  CUSTOMERS: 'customers_1771793184',
  CONTACT_DOCUMENTS: 'contact_documents_1771793184',
  MANUFACTURER_IMPORTERS: 'manufacturer_importers_1771793184',
  FIREARMS: 'firearms_1771793184',
  ENTRIES: 'bound_book_entries_1771793184',
  NOTES: 'entry_notes_1771793184',
  LOGS: 'audit_logs_1771793184',
  PENDING_ORDERS: 'pending_orders_1771793184',
  TENANT_API_KEYS: 'tenant_api_keys_1771793184'
};

export const api = {
  getProfile: async (userId) => {
    try {
      // 1. Check if any tenants exist at all
      const { count } = await supabase
        .from(TABLES.TENANTS)
        .select('*', { count: 'exact', head: true });

      const isInitialSetup = count === 0;

      // 2. Fetch the actual profile
      const { data: profile, error } = await supabase
        .from(TABLES.PROFILES)
        .select(`
          *,
          tenant:tenant_id (*)
        `)
        .eq('id', userId)
        .maybeSingle();
      
      if (error) throw error;
      
      // If profile exists, return it
      if (profile) {
        return { user: profile, tenant: profile.tenant };
      }

      // 3. BOOTSTRAP: If no profile exists and no tenants exist, 
      // treat this user as the Super Admin for setup
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      return { 
        user: { 
          id: userId, 
          email: authUser?.email, 
          role: isInitialSetup ? 'SUPER_ADMIN' : 'CLERK', 
          name: authUser?.user_metadata?.full_name || (isInitialSetup ? 'System Setup' : 'New User') 
        }, 
        tenant: null 
      };
    } catch (err) {
      console.error("Profile fetch error:", err);
      return { user: { id: userId, role: 'CLERK', name: 'User' }, tenant: null };
    }
  },

  getTenants: async () => {
    const { data, error } = await supabase
      .from(TABLES.TENANTS)
      .select('*')
      .order('company_name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  getTenantById: async (tenantId) => {
    const { data, error } = await supabase
      .from(TABLES.TENANTS)
      .select('*')
      .eq('id', tenantId)
      .single();
    if (error) throw error;
    return data;
  },

  createTenant: async (tenantData, creatorId) => {
    // 1. Create the tenant
    const { data: tenant, error: tError } = await supabase
      .from(TABLES.TENANTS)
      .insert({ ...tenantData, is_active: true })
      .select()
      .single();

    if (tError) throw tError;

    // 2. Only create a profile if the creator doesn't already have one
    const { data: existingProfile } = await supabase
      .from(TABLES.PROFILES)
      .select('id')
      .eq('id', creatorId)
      .maybeSingle();

    if (!existingProfile) {
      const { error: pError } = await supabase
        .from(TABLES.PROFILES)
        .insert({
          id: creatorId,
          tenant_id: tenant.id,
          email: tenantData.email,
          role: 'SUPER_ADMIN',
          name: 'System Admin'
        });
      if (pError) console.warn("Profile link failed:", pError.message);
    }

    return tenant;
  },

  updateTenant: async (id, updates) => {
    const { error } = await supabase
      .from(TABLES.TENANTS)
      .update(updates)
      .eq('id', id);
    if (error) throw error;
  },

  deleteTenant: async (id) => {
    // Storage rows can't be deleted from SQL (Supabase requires the Storage
    // API), so remove the tenant's files first. Failures here only orphan
    // files; the RPC below still removes every database row.
    try {
      const { data: docs } = await supabase
        .from(TABLES.CONTACT_DOCUMENTS)
        .select('file_path')
        .eq('tenant_id', id);
      if (docs?.length) {
        await supabase.storage
          .from('contact-documents')
          .remove(docs.map(d => d.file_path));
      }
      const { data: logoFiles } = await supabase.storage
        .from('tenant-logos')
        .list(id);
      if (logoFiles?.length) {
        await supabase.storage
          .from('tenant-logos')
          .remove(logoFiles.map(f => `${id}/${f.name}`));
      }
    } catch (storageErr) {
      console.error('Tenant storage cleanup failed:', storageErr);
    }

    // Tenants table has no DELETE policy and child tables have NO ACTION FKs;
    // deletion must go through the super-admin-only RPC.
    const { error } = await supabase.rpc('delete_tenant_1771793184', { p_tenant_id: id });
    if (error) throw error;
  },

  uploadTenantLogo: async (tenantId, file) => {
    const fileExt = file.name?.split('.').pop() || 'png';
    const filePath = `${tenantId}/logo.${fileExt}`;

    // Delete existing logo files for this tenant first
    const { data: existingFiles } = await supabase.storage
      .from('tenant-logos')
      .list(tenantId);
    if (existingFiles?.length) {
      await supabase.storage
        .from('tenant-logos')
        .remove(existingFiles.map(f => `${tenantId}/${f.name}`));
    }

    // Upload new logo
    const { error: uploadError } = await supabase.storage
      .from('tenant-logos')
      .upload(filePath, file, { upsert: true, contentType: file.type || 'image/png' });
    if (uploadError) throw uploadError;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('tenant-logos')
      .getPublicUrl(filePath);

    // Add cache-busting param
    const logoUrl = `${publicUrl}?t=${Date.now()}`;

    // Update tenant record
    const { data: updated, error: updateError } = await supabase
      .from(TABLES.TENANTS)
      .update({ logo_url: logoUrl })
      .eq('id', tenantId)
      .select();
    if (updateError) throw updateError;
    if (!updated?.length) throw new Error('Failed to save logo — you may not have permission to update this company.');

    return logoUrl;
  },

  removeTenantLogo: async (tenantId) => {
    // Delete files from storage
    const { data: existingFiles } = await supabase.storage
      .from('tenant-logos')
      .list(tenantId);
    if (existingFiles?.length) {
      await supabase.storage
        .from('tenant-logos')
        .remove(existingFiles.map(f => `${tenantId}/${f.name}`));
    }

    // Clear logo_url on tenant
    const { data: cleared, error } = await supabase
      .from(TABLES.TENANTS)
      .update({ logo_url: null })
      .eq('id', tenantId)
      .select();
    if (error) throw error;
    if (!cleared?.length) throw new Error('Failed to remove logo — you may not have permission to update this company.');
  },

  getUsers: async (tenantId) => {
    let query = supabase
      .from(TABLES.PROFILES)
      .select('*, tenant:tenant_id (id, company_name)')
      .order('created_at', { ascending: false });
    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  updateUserRole: async (userId, role) => {
    const { data, error } = await supabase
      .from(TABLES.PROFILES)
      .update({ role })
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  createUser: async (email, password, name, tenantId, role) => {
    // Server-side via the create-user edge function (service role): auth user
    // and profile are created together, orphaned auth users are adopted, and
    // the admin's session is never touched. Authorization (super admin, or
    // owner of the target tenant) is enforced in the function.
    const { data, error } = await supabase.functions.invoke('create-user', {
      body: { email, password, name, tenant_id: tenantId, role: role || 'CLERK' }
    });
    if (error) {
      // Non-2xx responses land here; surface the function's error message.
      let message = error.message;
      try {
        const ctx = await error.context?.json();
        if (ctx?.error) message = ctx.error;
      } catch { /* keep generic message */ }
      throw new Error(message);
    }
    if (data?.error) throw new Error(data.error);
    return data.profile;
  },

  deleteUser: async (userId) => {
    // Server-side via the delete-user edge function (service role): removes
    // the auth account too (profile follows via FK cascade), so the email can
    // be re-used. Users with bound book/audit history are refused server-side
    // and must be deactivated instead.
    const { data, error } = await supabase.functions.invoke('delete-user', {
      body: { user_id: userId }
    });
    if (error) {
      let message = error.message;
      try {
        const ctx = await error.context?.json();
        if (ctx?.error) message = ctx.error;
      } catch { /* keep generic message */ }
      throw new Error(message);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  },

  updateUserStatus: async (userId, isActive) => {
    const { data, error } = await supabase
      .from(TABLES.PROFILES)
      .update({ is_active: isActive })
      .eq('id', userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  getBoundBook: async (tenantId) => {
    if (!tenantId) return [];
    const { data, error } = await supabase
      .from(TABLES.ENTRIES)
      .select(`
        *,
        firearm:firearms_1771793184 (
          id,
          serial_number,
          is_non_serialized,
          manufacturer_id,
          importer_id,
          model_id,
          type_id,
          caliber_id,
          manufacturer:manufacturers_1771793184 (name, is_domestic),
          importer:importers_1771793184 (name, city, state),
          model:firearm_models_1771793184 (model_name),
          type:firearm_types_1771793184 (name),
          caliber:calibers_1771793184 (name)
        ),
        acquired_from:customers_1771793184!acquired_from_id (name, contact_name, ffl_number, phone, email, address, type),
        disposed_to:customers_1771793184!disposed_to_id (name, contact_name, ffl_number, phone, email, address, type)
      `)
      .eq('tenant_id', tenantId)
      .order('acquisition_date', { ascending: false });

    if (error) throw error;
    return (data || []).map(entry => ({
      ...entry,
      firearm_id: entry.firearm?.id,
      serial_number: entry.firearm?.serial_number,
      is_non_serialized: entry.firearm?.is_non_serialized,
      manufacturer_id: entry.firearm?.manufacturer_id,
      importer_id: entry.firearm?.importer_id,
      model_id: entry.firearm?.model_id,
      type_id: entry.firearm?.type_id,
      caliber_id: entry.firearm?.caliber_id,
      manufacturer: entry.firearm?.manufacturer?.name,
      importer: entry.firearm?.importer?.name,
      model: entry.firearm?.model?.model_name,
      type: entry.firearm?.type?.name,
      caliber: entry.firearm?.caliber?.name,
      acquired_from_name: entry.acquired_from?.name,
      acquired_from_detail: entry.acquired_from,
      disposed_to_name: entry.disposed_to?.name,
      disposed_to_detail: entry.disposed_to
    }));
  },

  getLookups: async (table, tenantId, filters = {}) => {
    const tableName = TABLES[table.toUpperCase()];
    const isModels = table.toUpperCase() === 'MODELS';
    const orderCol = isModels ? 'model_name' : 'name';

    let query = supabase
      .from(tableName)
      .select('*')
      .order(orderCol, { ascending: true });

    if (tenantId) {
      query = query.eq('tenant_id', tenantId);
    }

    if (isModels && filters.manufacturer_id) {
      query = query.eq('manufacturer_id', filters.manufacturer_id);
    }

    const { data, error } = await query;
    if (error) throw error;

    if (isModels) {
      return (data || []).map(d => ({ ...d, name: d.model_name }));
    }
    return data || [];
  },

  createLookup: async (table, tenantId, name, extras = {}) => {
    const tableName = TABLES[table.toUpperCase()];
    const isModels = table.toUpperCase() === 'MODELS';

    const row = isModels
      ? { tenant_id: tenantId, model_name: name, ...extras }
      : { tenant_id: tenantId, name, ...extras };

    const { data, error } = await supabase
      .from(tableName)
      .insert(row)
      .select()
      .single();
    if (error) throw error;

    if (isModels) {
      return { ...data, name: data.model_name };
    }
    return data;
  },

  updateLookup: async (table, id, name, extras = {}) => {
    const tableName = TABLES[table.toUpperCase()];
    const isModels = table.toUpperCase() === 'MODELS';
    const updateObj = isModels ? { model_name: name, ...extras } : { name, ...extras };

    const { data, error } = await supabase
      .from(tableName)
      .update(updateObj)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    if (isModels) {
      return { ...data, name: data.model_name };
    }
    return data;
  },

  // Manufacturer-Importer relationships
  getManufacturerImporters: async (manufacturerId) => {
    const { data, error } = await supabase
      .from(TABLES.MANUFACTURER_IMPORTERS)
      .select(`
        *,
        importer:importers_1771793184 (id, name, city, state)
      `)
      .eq('manufacturer_id', manufacturerId)
      .eq('is_current', true)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  linkManufacturerImporter: async (tenantId, manufacturerId, importerId, notes = '') => {
    const { data, error } = await supabase
      .from(TABLES.MANUFACTURER_IMPORTERS)
      .insert({
        tenant_id: tenantId,
        manufacturer_id: manufacturerId,
        importer_id: importerId,
        notes: notes || null
      })
      .select(`
        *,
        importer:importers_1771793184 (id, name, city, state)
      `)
      .single();
    if (error) throw error;
    return data;
  },

  unlinkManufacturerImporter: async (id) => {
    const { error } = await supabase
      .from(TABLES.MANUFACTURER_IMPORTERS)
      .update({ is_current: false })
      .eq('id', id);
    if (error) throw error;
  },

  // Contact (Customer) management
  getContacts: async (tenantId) => {
    if (!tenantId) return [];
    const { data, error } = await supabase
      .from(TABLES.CUSTOMERS)
      .select('*')
      .eq('tenant_id', tenantId)
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  // Tenant-scoped paged search over contacts. Empty search returns the most
  // recently created contacts. Used by the ContactSelect combobox so we don't
  // load thousands of records on every page.
  searchContacts: async (tenantId, { search = '', limit = 50 } = {}) => {
    if (!tenantId) return [];
    const term = (search || '').trim();
    let q = supabase
      .from(TABLES.CUSTOMERS)
      .select('*')
      .eq('tenant_id', tenantId);
    if (term) {
      const pat = `%${term}%`;
      q = q
        .or(`name.ilike.${pat},contact_name.ilike.${pat},ffl_number.ilike.${pat},email.ilike.${pat}`)
        .order('name', { ascending: true });
    } else {
      q = q.order('created_at', { ascending: false });
    }
    const { data, error } = await q.limit(limit);
    if (error) throw error;
    return data || [];
  },

  getContactById: async (id) => {
    if (!id) return null;
    const { data, error } = await supabase
      .from(TABLES.CUSTOMERS)
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  createContact: async (tenantId, contactData) => {
    const { data, error } = await supabase
      .from(TABLES.CUSTOMERS)
      .insert({ tenant_id: tenantId, ...contactData })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  updateContact: async (contactId, contactData) => {
    const { data, error } = await supabase
      .from(TABLES.CUSTOMERS)
      .update(contactData)
      .eq('id', contactId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // National FFL directory lookup (shared reference data — NOT tenant-scoped).
  // Reads go through SECURITY DEFINER RPCs that expose only a curated column
  // set; the directory tables themselves are not readable by the client.
  searchFflDirectory: async (query, limit = 20) => {
    const q = (query || '').trim();
    if (q.length < 2) return [];
    const { data, error } = await supabase.rpc('search_ffl_directory', {
      p_query: q,
      p_limit: limit
    });
    if (error) throw error;
    return data || [];
  },

  // Re-verify a single FFL number against the directory. Returns the directory
  // record (with is_active / is_expired flags) or null when not found.
  verifyFfl: async (fflNumber) => {
    const n = (fflNumber || '').trim();
    if (!n) return null;
    const { data, error } = await supabase.rpc('verify_ffl', { p_ffl_number: n });
    if (error) throw error;
    return (data && data[0]) || null;
  },

  // Contact document management
  getContactDocuments: async (contactId) => {
    const { data, error } = await supabase
      .from(TABLES.CONTACT_DOCUMENTS)
      .select('*')
      .eq('contact_id', contactId)
      .order('uploaded_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  uploadContactDocument: async (tenantId, contactId, file, documentType) => {
    const timestamp = Date.now();
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `${tenantId}/${contactId}/${timestamp}_${sanitizedName}`;

    const { error: uploadError } = await supabase.storage
      .from('contact-documents')
      .upload(filePath, file, { contentType: file.type });
    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('contact-documents')
      .getPublicUrl(filePath);

    const { data, error } = await supabase
      .from(TABLES.CONTACT_DOCUMENTS)
      .insert({
        tenant_id: tenantId,
        contact_id: contactId,
        file_name: file.name,
        file_path: filePath,
        file_type: file.type,
        file_url: publicUrl,
        document_type: documentType
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  deleteContactDocument: async (documentId, filePath) => {
    const { error: storageError } = await supabase.storage
      .from('contact-documents')
      .remove([filePath]);
    if (storageError) throw storageError;

    const { error } = await supabase
      .from(TABLES.CONTACT_DOCUMENTS)
      .delete()
      .eq('id', documentId);
    if (error) throw error;
  },

  addAcquisition: async (tenantId, userId, formData) => {
    // 1. Use the selected contact ID directly
    const customerId = formData.acquired_from_id;
    if (!customerId) throw new Error('A contact must be selected for the acquisition source.');
    const missing = [
      ['manufacturer_id', 'Manufacturer'], ['model_id', 'Model'],
      ['type_id', 'Firearm Type'], ['caliber_id', 'Caliber']
    ].filter(([key]) => !formData[key]).map(([, name]) => name);
    if (missing.length) {
      throw new Error(`Select a value for: ${missing.join(', ')}. If you typed a new one, click Add to save it first.`);
    }

    // 2. Create the firearm record
    const isNSN = !!formData.is_non_serialized;
    const serialNumber = isNSN
      ? `NSN-${crypto.randomUUID()}`
      : formData.serial_number;

    const { data: firearm, error: fErr } = await supabase
      .from(TABLES.FIREARMS)
      .insert({
        tenant_id: tenantId,
        serial_number: serialNumber,
        is_non_serialized: isNSN,
        manufacturer_id: formData.manufacturer_id,
        importer_id: formData.importer_id || null,
        model_id: formData.model_id,
        type_id: formData.type_id,
        caliber_id: formData.caliber_id
      })
      .select()
      .single();
    if (fErr) throw fErr;

    // 3. Create the bound book entry
    const { data: entry, error: eErr } = await supabase
      .from(TABLES.ENTRIES)
      .insert({
        tenant_id: tenantId,
        firearm_id: firearm.id,
        acquisition_date: formData.acquisition_date,
        acquisition_type: formData.acquisition_type || 'NEW',
        acquired_from_id: customerId,
        status: 'IN_HOUSE',
        created_by: userId
      })
      .select()
      .single();
    if (eErr) {
      // The entry can be rejected (e.g. this firearm is already in house), and
      // the two inserts aren't one transaction — don't leave an orphan firearm.
      await supabase.from(TABLES.FIREARMS).delete().eq('id', firearm.id).eq('tenant_id', tenantId);
      throw eErr;
    }

    return entry;
  },

  // Earlier bound book entries for a serial number (trimmed, case-insensitive),
  // newest first, with the firearm details recorded on each visit.
  getFirearmHistory: async (tenantId, serialNumber) => {
    const serial = (serialNumber || '').trim();
    if (!tenantId || !serial) return [];
    // ilike without wildcards = case-insensitive equality; escape the
    // characters ilike treats as wildcards.
    const pattern = serial.replace(/[\\%_]/g, (c) => `\\${c}`);
    const { data, error } = await supabase
      .from(TABLES.ENTRIES)
      .select(`
        id, status, acquisition_date, disposition_date,
        firearm:firearms_1771793184!inner (
          id, serial_number, is_non_serialized,
          manufacturer_id, importer_id, model_id, type_id, caliber_id,
          manufacturer:manufacturers_1771793184 (id, name, is_domestic),
          model:firearm_models_1771793184 (model_name),
          caliber:calibers_1771793184 (name)
        )
      `)
      .eq('tenant_id', tenantId)
      .eq('firearm.is_non_serialized', false)
      .ilike('firearm.serial_number', pattern)
      .order('acquisition_date', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  disposeFirearm: async (tenantId, entryId, formData) => {
    // 1. Use the selected contact ID directly
    const customerId = formData.disposed_to_id;
    if (!customerId) throw new Error('A contact must be selected for the disposition recipient.');

    // 2. Update the bound book entry
    const { data, error } = await supabase
      .from(TABLES.ENTRIES)
      .update({
        disposed_to_id: customerId,
        disposition_date: formData.disposition_date,
        form_4473_reference: formData.form_4473_reference || null,
        status: 'DISPOSED',
        closed_at: new Date().toISOString()
      })
      .eq('id', entryId)
      .eq('tenant_id', tenantId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  getStats: async (tenantId) => {
    if (!tenantId) return { total: 0, inHouse: 0, disposed: 0, recent: [] };
    const { data, error } = await supabase
      .from(TABLES.ENTRIES)
      .select(`
        id, status, acquisition_date, disposition_date,
        firearm:firearms_1771793184 (
          serial_number,
          manufacturer:manufacturers_1771793184 (name),
          model:firearm_models_1771793184 (model_name)
        )
      `)
      .eq('tenant_id', tenantId);

    if (error) throw error;
    const stats = data || [];

    // Build daily chart data for last 30 days
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const dailyMap = {};
    for (let d = new Date(thirtyDaysAgo); d <= now; d.setDate(d.getDate() + 1)) {
      const key = todayLocal(d);
      dailyMap[key] = { date: key, acquisitions: 0, dispositions: 0 };
    }

    stats.forEach(item => {
      const acqDate = item.acquisition_date;
      if (acqDate && dailyMap[acqDate]) {
        dailyMap[acqDate].acquisitions++;
      }
      const dispDate = item.disposition_date;
      if (dispDate && dailyMap[dispDate]) {
        dailyMap[dispDate].dispositions++;
      }
    });

    const chartData = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));

    // Totals for the 30-day period
    const totalAcquisitions30d = chartData.reduce((s, d) => s + d.acquisitions, 0);
    const totalDispositions30d = chartData.reduce((s, d) => s + d.dispositions, 0);

    return {
      total: stats.length,
      inHouse: stats.filter(i => i.status === 'IN_HOUSE').length,
      disposed: stats.filter(i => i.status === 'DISPOSED').length,
      chartData,
      totalAcquisitions30d,
      totalDispositions30d,
      recent: stats.slice(0, 5).map(item => ({
        id: item.id,
        acquisition_date: item.acquisition_date,
        status: item.status,
        serial_number: item.firearm?.serial_number,
        manufacturer: item.firearm?.manufacturer?.name,
        model: item.firearm?.model?.model_name
      }))
    };
  },

  // Entry Notes
  getEntryNotes: async (entryId) => {
    const { data, error } = await supabase
      .from(TABLES.NOTES)
      .select('*')
      .eq('entry_id', entryId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  updateBoundBookEntry: async (tenantId, record, changes, userId, userName) => {
    const changeNotes = [];

    // Separate firearm-level vs entry-level changes
    const firearmFields = ['serial_number', 'is_non_serialized', 'manufacturer_id', 'importer_id', 'model_id', 'type_id', 'caliber_id'];
    const entryFields = ['acquisition_date', 'acquisition_type', 'acquired_from_id', 'disposition_date', 'disposed_to_id', 'form_4473_reference'];

    const firearmUpdate = {};
    const entryUpdate = {};

    for (const [key, val] of Object.entries(changes)) {
      if (firearmFields.includes(key)) firearmUpdate[key] = val;
      if (entryFields.includes(key)) entryUpdate[key] = val;
    }

    // Update firearms table
    if (Object.keys(firearmUpdate).length > 0) {
      const { error } = await supabase
        .from(TABLES.FIREARMS)
        .update(firearmUpdate)
        .eq('id', record.firearm_id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    }

    // Update entries table
    if (Object.keys(entryUpdate).length > 0) {
      const { error } = await supabase
        .from(TABLES.ENTRIES)
        .update(entryUpdate)
        .eq('id', record.id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    }

    // Build human-readable change descriptions and save as audit note
    for (const desc of changes._descriptions || []) {
      changeNotes.push(desc);
    }

    if (changeNotes.length > 0) {
      const auditText = 'EDIT: ' + changeNotes.join('; ');
      await supabase
        .from(TABLES.NOTES)
        .insert({
          tenant_id: tenantId,
          entry_id: record.id,
          note: auditText,
          created_by: userId,
          created_by_name: userName
        });
    }
  },

  addEntryNote: async (tenantId, entryId, note, userId, userName) => {
    const { data, error } = await supabase
      .from(TABLES.NOTES)
      .insert({
        tenant_id: tenantId,
        entry_id: entryId,
        note,
        created_by: userId,
        created_by_name: userName
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  // ===== Pending Orders (WooCommerce staging) =====

  getPendingOrders: async (tenantId, { status, search } = {}) => {
    if (!tenantId) return [];
    let q = supabase
      .from(TABLES.PENDING_ORDERS)
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });

    if (Array.isArray(status) && status.length > 0) {
      q = q.in('status', status);
    } else if (typeof status === 'string') {
      q = q.eq('status', status);
    }

    if (search && search.trim()) {
      const term = `%${search.trim()}%`;
      q = q.or(
        `declared_customer_name.ilike.${term},declared_serial_number.ilike.${term},external_order_id.ilike.${term}`
      );
    }

    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },

  getPendingOrdersStats: async (tenantId) => {
    if (!tenantId) return { pending: 0, received: 0 };
    const { data, error } = await supabase
      .from(TABLES.PENDING_ORDERS)
      .select('status')
      .eq('tenant_id', tenantId)
      .in('status', ['PENDING', 'RECEIVED']);
    if (error) throw error;
    const rows = data || [];
    return {
      pending: rows.filter(r => r.status === 'PENDING').length,
      received: rows.filter(r => r.status === 'RECEIVED').length
    };
  },

  getPendingOrder: async (id) => {
    const { data, error } = await supabase
      .from(TABLES.PENDING_ORDERS)
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  markPendingOrderReceived: async (id, userId) => {
    const { data, error } = await supabase
      .from(TABLES.PENDING_ORDERS)
      .update({
        status: 'RECEIVED',
        received_at: new Date().toISOString(),
        received_by: userId
      })
      .eq('id', id)
      .eq('status', 'PENDING')
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  cancelPendingOrder: async (id, reason) => {
    const { data, error } = await supabase
      .from(TABLES.PENDING_ORDERS)
      .update({
        status: 'CANCELED',
        cancel_reason: reason || null
      })
      .eq('id', id)
      .in('status', ['PENDING', 'RECEIVED'])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  convertPendingOrderToAcquisition: async (pendingId, verifiedData) => {
    const { data, error } = await supabase.rpc(
      'convert_pending_order_1771793184',
      { p_pending_id: pendingId, p_verified: verifiedData }
    );
    if (error) throw error;
    return data;
  },

  // ===== Tenant API Keys =====

  getTenantApiKeys: async (tenantId) => {
    if (!tenantId) return [];
    const { data, error } = await supabase
      .from(TABLES.TENANT_API_KEYS)
      .select('id, tenant_id, name, key_prefix, last_used_at, created_by, created_at, revoked_at')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  createTenantApiKey: async (name, tenantId = null) => {
    const { data, error } = await supabase.rpc(
      'create_tenant_api_key_1771793184',
      { p_name: name, p_tenant_id: tenantId }
    );
    if (error) throw error;
    return data;
  },

  revokeTenantApiKey: async (keyId) => {
    const { error } = await supabase.rpc(
      'revoke_tenant_api_key_1771793184',
      { p_key_id: keyId }
    );
    if (error) throw error;
  }
};